import { Controller } from '@hotwired/stimulus';
import { application } from 'src';
import { find, addClass, removeClass, findAll } from '@fretadao/f-js-dom';

application.register(
  'admin-modal',
  class extends Controller {
    static values = {
      formName: { type: String, default: 'admin-form' },
      title: String,
      fields: Object,
      confirmButton: Object,
      cancelButton: Object,
      text: String
    };
    static targets = ['description', 'submit'];

    createModal(event) {
      if (find('#admin-modal')) return;

      event.stopPropagation();
      event.preventDefault();
      this.#modalHTML();
      this.#setupEvents();
    }

    #modalHTML() {
      const modal = document.createElement('div');
      const modalClasses = [
        'ui-dialog', 'ui-corner-all', 'ui-widget', 'ui-widget-content', 'ui-front',
        'active_admin_dialog', 'ui-dialog-buttons', 'ui-draggable', 'ui-resizable'
      ];
      modalClasses.forEach(klass => addClass(modal, klass));
      modal.id = 'admin-modal';
      modal.tabIndex = -1;
      modal.role = 'dialog';
      modal.style = 'position: fixed; height: auto; width: 400px; top: 30%; left: 30%';
      modal.setAttribute('data-controller', 'ga-tracker');

      modal.appendChild(this.#titleModal())

      if (this.hasFieldsValue) {
        modal.appendChild(this.#form());
      } else {
        modal.appendChild(this.#confirmMessage())
      }
      modal.appendChild(this.#buttons());

      find('#wrapper').appendChild(modal);
    }

    #titleModal() {
      const titleContainer = document.createElement('div');
      const titleText = document.createElement('span');
      const containerClasses = [
        'ui-dialog-titlebar', 'ui-corner-all', 'ui-widget-header',
        'ui-helper-clearfix', 'ui-draggable-handle'
      ];
      const textClasses = ['ui-dialog-title'];

      addClass(titleContainer, containerClasses);
      addClass(titleText, textClasses);

      titleContainer.appendChild(titleText);
      titleText.innerText = this.titleValue;

      return titleContainer;
    }

    #form() {
      const form = document.createElement('form');
      form.action = this.confirmButtonValue?.path;
      form.method = this.confirmButtonValue?.method || 'get';
      form.name = this.formNameValue;

      let inputToken = document.createElement('input');
      inputToken.name = 'authenticity_token';
      inputToken.type = 'hidden';
      inputToken.value = find('meta[name="csrf-token"]').getAttribute('content');
      form.appendChild(inputToken);

      if (this.hasFieldsValue) {
        Object.keys(this.fieldsValue).forEach((field) => {
          let fieldParams = this.fieldsValue[field];
          let container = document.createElement('div');
          container.style = 'display: flex; justify-content: space-between; margin-bottom: 3px';

          container.appendChild(this.#label(fieldParams, field));

          let containerField = document.createElement('div');
          containerField.style.width = '70%';

          if(fieldParams.type === 'radio' && Array.isArray(fieldParams.options)) {
            fieldParams.options.forEach((option, index) => {
              let containerOption = document.createElement('div');
              let params = Object.assign(fieldParams, option, { name: field });
              containerOption.appendChild(this.#field(params, `${field}_${index}`, form));
              containerOption.appendChild(this.#label(params, `${field}_${index}`, { 'font-weight': 'normal', 'margin-left': '10px' }));
              containerField.appendChild(containerOption);
            });
          } else {
            containerField.appendChild(this.#field(fieldParams, field, form));

            if(fieldParams['hint']) {
              containerField.appendChild(this.#hint(fieldParams, field, form));
            }
          }
          container.appendChild(containerField);

          form.appendChild(container);
        });
      }

      return form;
    }

    #confirmMessage() {
      const messageContainer = document.createElement('div');
      const textBox = document.createElement('h3');

      textBox.innerHTML = this.textValue;
      messageContainer.appendChild(textBox);
      messageContainer.style = 'margin-left: 32px; margin-right: 32px;';

      findAll('.description-target').forEach(element => {
        const descriptionContainer = document.createElement('h4');
        const descriptionElement = document.createElement('b');
        descriptionElement.innerText = element.getAttribute('description-content');
        descriptionContainer.appendChild(descriptionElement);
        messageContainer.appendChild(descriptionContainer);
      });

      return messageContainer;
    }

    #field(fieldParams, fieldName, form) {
      const field = document.createElement('input');
      field.name = field.id = `${fieldName}_id`;

      Object.keys(fieldParams).forEach(attribute => field[attribute] = fieldParams[attribute])

      if(fieldParams.show_if) {
        field.style.display = 'none';
        field.required = false;

        findAll(fieldParams.show_if.field, form).forEach((element) => {
          element.addEventListener('change', (e) => {
            if(e.target.value === fieldParams.show_if.eq) {
              field.style.display = 'block';
              if(fieldParams.required) field.required = true;
              find(`label[for=${field.id}]`, form).style.display = 'block';
              find(`small[id=${field.name}_hint_id]`, form).style.display = 'block';
            } else {
              field.style.display = 'none';
              field.required = false;
              if(['radio', 'checkbox'].includes(field.type)) {
                field.checked = false;
              } else {
                field.value = '';
              }
              find(`label[for=${field.id}]`, form).style.display = 'none';
              find(`small[id=${field.name}_hint_id]`, form).style.display = 'none';
            }
          })
        })
      }

      return field;
    }

    #label(fieldParams, fieldName, customStyles) {
      const label = document.createElement('label');
      const styleBase = { 'color': '#383838', 'font-size': '1.0em', 'font-weight': 'bold', 'max-width': '25%' };
      if(fieldParams.show_if) styleBase.display = 'none';

      label.classList.add('label');
      label.setAttribute('for', `${fieldName}_id`);
      label.innerText = fieldParams.label;
      label.style = Object.entries(Object.assign(styleBase, customStyles)).map((arr) => arr.join(':')).join(';');

      return label;
    }

    #buttons() {
      const container = document.createElement('div');
      const buttonsContainer = document.createElement('div');
      const confirmButton = this.#createConfirmButton();
      const cancelButton = this.#createCancelButton();

      addClass(container, ['ui-dialog-buttonpane', 'ui-widget-content', 'ui-helper-clearfix']);

      addClass(buttonsContainer, 'ui-dialog-buttonset');

      buttonsContainer.appendChild(cancelButton);
      buttonsContainer.appendChild(confirmButton);

      container.appendChild(buttonsContainer);

      return container;
    }

    #createConfirmButton() {
      const confirmButtonContainer = document.createElement('a');
      const confirmButton = document.createElement('button');
      const attributes = this.confirmButtonValue?.attrs;

      confirmButtonContainer.appendChild(confirmButton);
      confirmButtonContainer.id = 'confirm-button-container';

      confirmButton.innerText = this.confirmButtonValue.text || 'Confirmar';
      confirmButton.id = 'confirm-button';
      confirmButton.type = 'button';
      confirmButton.style = 'background: #0052cc; color: white;';

      if (attributes) Object.entries(attributes).forEach((entry) => confirmButton.setAttribute(entry[0], entry[1]));

      addClass(confirmButton, ['ui-button', 'ui-corner-all', 'ui-widget']);

      return confirmButtonContainer;
    }

    #createCancelButton() {
      const cancelButton = document.createElement('button');

      cancelButton.innerText = this.cancelButtonValue.text || 'Cancelar';
      cancelButton.id = 'cancel-button';
      cancelButton.type = 'button';
      cancelButton.style.color = '#0052cc';
      cancelButton.style.background = 'white';

      cancelButton.addEventListener('mouseover', () => cancelButton.style.background = '#dfe2e5');
      cancelButton.addEventListener('mouseout', () => cancelButton.style.background = 'white');

      addClass(cancelButton, ['ui-button', 'ui-corner-all', 'ui-widget']);

      return cancelButton;
    }

    #setupEvents() {
      const confirmButton = find('#confirm-button');
      const cancelButton = find('#cancel-button');

      confirmButton.addEventListener('click', () => {
        addClass(confirmButton, 'disabled');
        confirmButton.setAttribute('disabled', true);

        if (this.hasConfirmButtonValue) {
          const form = document.forms[this.formNameValue];

          if (!form) return this.#addHrefToButton();

          if (form.checkValidity()) {
            find("#admin-modal form").submit();
          }
          else {
            this.#enableConfirmButton();
            form.reportValidity();
          }
        } else this.submitTarget.form.submit();
      });
      cancelButton.addEventListener('click', () => this.#dismissModal());
    }

    #enableConfirmButton() {
      removeClass(confirmButton, 'disabled');
      confirmButton.setAttribute('disabled', false);
    }

    #addHrefToButton() {
      const confirmButton = find('#confirm-button-container');
      const path = this.confirmButtonValue.path;
      let params = {};

      Object.keys(this.fieldsValue).forEach((field) => {
        const element = find(`#${field}_id`);

        if (element) params[field] = element.value;
      });

      confirmButton.href = `${path}&${this.#buildQueryString(params)}`;
    }

    #buildQueryString(params) {
      let queryString = '';

      Object.keys(params).forEach((key) => {
        if (!queryString) queryString += '&';
        queryString += `${key}=${params[key]}`;
      });

      return queryString;
    }

    #dismissModal() {
      findAll('#admin-modal').forEach(modal => find('#wrapper').removeChild(modal))
    }

    #hint(fieldParams, fieldName, form) {
      const hint = document.createElement('small');
      hint.style = { 'max-width': '70%' }
      if(fieldParams.show_if) hint.style.display = 'none';
      hint.name = hint.id = `${fieldName}_hint_id`;
      hint.innerText = fieldParams['hint'];

      return hint;
    }
  }
)
